import { useState, type KeyboardEvent } from "react";
import {
  useCreate,
  useCreateSuggestionContext,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { Company } from "../types";
import {
  findExistingCompany,
  normalizeWebsite,
  websiteFromEmail,
} from "./companyQuickCreate";

/**
 * "New company" from the company picker: a name and a website, without leaving
 * the form the picker is in. LCCRM-87.
 *
 * The website is what gives the company its logo and what a duplicate is
 * noticed by, so it is asked for here and suggested from the contact's email
 * when the address is not at a mail provider. A company the details already
 * describe is offered instead of being made a second time.
 *
 * Deliberately not a <form>: this renders inside the contact (or deal) form,
 * and React submits bubble through portals, so a nested form would submit the
 * outer one.
 */
export const CompanyQuickCreateDialog = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  const [create, { isPending }] = useCreate<Company>();
  // The form the picker sits in. On the contact form its `email` is the
  // contact's, read once as the dialog opens; a deal form has no such field.
  const outerForm = useFormContext();
  const [name, setName] = useState(filter ?? "");
  const [website, setWebsite] = useState(() =>
    websiteFromEmail(outerForm?.getValues("email")),
  );

  // The whole list, once: matching on a normalised name cannot be asked of the
  // backend as a filter. The contact import does the same.
  const { data: companies } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "name", order: "ASC" },
  });
  const existing = findExistingCompany(companies, { name, website });

  const canCreate = name.trim() !== "" && !isPending;

  const handleCreate = async () => {
    if (!canCreate) return;
    try {
      const company = await create(
        "companies",
        {
          data: {
            name: name.trim(),
            website: normalizeWebsite(website),
            sales_id: identity?.id,
            created_at: new Date().toISOString(),
          },
        },
        { returnPromise: true },
      );
      onCreate(company);
    } catch {
      notify("resources.companies.autocomplete.create_error", {
        type: "error",
        messageArgs: {
          _: "An error occurred while creating the company",
        },
      });
    }
  };

  // Enter does what the highlighted button does.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (existing) onCreate(existing);
    else void handleCreate();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("resources.companies.quick_create.title", {
              _: "New company",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate("resources.companies.quick_create.description", {
              _: "The website gives the company its logo and stops it being added twice.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm font-medium">
            {translate("resources.companies.fields.name", { _: "Name" })}
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            {translate("resources.companies.fields.website", {
              _: "Website",
            })}
            <Input
              value={website}
              placeholder="https://"
              onChange={(event) => setWebsite(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </label>
          {existing && (
            <p role="status" className="text-sm text-muted-foreground">
              {translate("resources.companies.quick_create.exists", {
                name: existing.name,
                _: "%{name} is already in the CRM.",
              })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {translate("ra.action.cancel")}
          </Button>
          {existing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCreate}
                disabled={!canCreate}
              >
                {translate("resources.companies.quick_create.create_anyway", {
                  _: "Create anyway",
                })}
              </Button>
              <Button type="button" onClick={() => onCreate(existing)}>
                {translate("resources.companies.quick_create.use_existing", {
                  name: existing.name,
                  _: "Use %{name}",
                })}
              </Button>
            </>
          ) : (
            <Button type="button" onClick={handleCreate} disabled={!canCreate}>
              {isPending && <Loader2 className="size-4 animate-spin" />}
              {translate("resources.companies.quick_create.create", {
                _: "Create company",
              })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
